import React, { useEffect, useState, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access') || localStorage.getItem('gogig_admin_token');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  return null;
}

// Flatten report into rows: [{ company_name, establishment_code, candidate_id, name, mobile, total_points, activity }]
function flattenReport(report) {
  const rows = [];
  for (const company of report || []) {
    for (const c of company.candidates || []) {
      // Prefer per-candidate company_name (from documents when unlinked); else use section company_name
      const companyName = (c.company_name != null && String(c.company_name).trim() !== '')
        ? c.company_name
        : (company.company_name || '—');
      rows.push({
        company_name: companyName,
        establishment_code: company.establishment_code || '',
        candidate_id: c.candidate_id,
        name: c.name || '—',
        mobile: c.mobile || '—',
        total_points: c.total_points ?? 0,
        games_played: c.activity?.games_played ?? 0,
        quizzes_taken: c.activity?.quizzes_taken ?? 0,
        check_ins: c.activity?.check_ins ?? 0,
        stipend_attendance: c.activity?.stipend_attendance ?? 0,
        referral: c.activity?.referral ?? 0,
        other: c.activity?.other ?? 0,
        vouchers_claimed: c.activity?.vouchers_claimed ?? 0,
        total_activities: c.activity?.total_activities ?? 0
      });
    }
  }
  // Sort by company name, then by points descending
  rows.sort((a, b) => {
    if (a.company_name !== b.company_name) return a.company_name.localeCompare(b.company_name);
    return (b.total_points || 0) - (a.total_points || 0);
  });
  return rows;
}

function downloadCSV(rows) {
  const header = 'Company Name,Establishment Code,Candidate Name,Mobile,Total Points,Games Played,Quizzes Taken,Check-ins,Stipend/Attendance,Referral,Other,Vouchers Claimed,Total Activities';
  const escapeCsv = (v) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csvLines = [header, ...rows.map(r =>
    [r.company_name, r.establishment_code, r.name, r.mobile, r.total_points, r.games_played, r.quizzes_taken, r.check_ins, r.stipend_attendance, r.referral, r.other, r.vouchers_claimed, r.total_activities].map(escapeCsv).join(',')
  )];
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `gaming_report_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZES = [10, 25, 50, 100];

function AdminGamingReport({ apiBase, getToken }) {
  const tokenGetter = getToken && typeof getToken === 'function' ? getToken : getAdminAccessToken;
  const reportUrl = apiBase ? `${API_URL}/${apiBase}/gaming-report` : `${API_URL}/api/admin/gaming-report`;
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const flatRows = useMemo(() => flattenReport(report), [report]);
  const totalPages = Math.ceil(flatRows.length / pageSize) || 1;
  const startIdx = (page - 1) * pageSize;
  const paginatedRows = useMemo(() => flatRows.slice(startIdx, startIdx + pageSize), [flatRows, startIdx, pageSize]);

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [flatRows.length]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await tokenGetter();
      if (!token) {
        setError('Not authenticated. Please log in again.');
        setLoading(false);
        return;
      }
      if (!reportUrl || reportUrl.startsWith('undefined')) {
        setError('API URL not configured. Set REACT_APP_API_URL.');
        setLoading(false);
        return;
      }
      const res = await fetch(reportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store'  // avoid 304 Not Modified (no body) so we always get report data
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || data.error || `Request failed (${res.status}). Please try again.`);
        setLoading(false);
        return;
      }
      if (data.success) {
        setReport(Array.isArray(data.report) ? data.report : []);
      } else {
        setError(data.message || 'Failed to fetch gaming report');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch gaming report. Check server and network.');
      console.error('Gaming report fetch error:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#64748b', margin: 0 }}>Loading gaming report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 24, color: '#1e293b', margin: '0 0 8px 0' }}>
            Gaming Report
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: 14 }}>
            Candidate-wise points and activity. Activity includes: games, quizzes, check-ins, stipend/attendance, referral, and other sources.
          </p>
        </div>
        {flatRows.length > 0 && (
          <button
            type="button"
            onClick={() => downloadCSV(flatRows)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99,102,241,0.4)'
            }}
          >
            Download CSV
          </button>
        )}
      </div>

      {flatRows.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', background: '#f8fafc', borderRadius: 12, border: '1px dashed #e2e8f0' }}>
          <p style={{ color: '#1e293b', margin: '0 0 12px 0', fontWeight: 600 }}>No candidate rows in report</p>
          <p style={{ color: '#64748b', margin: '0 auto', maxWidth: 520 }}>
            {report.length > 0
              ? 'Companies are loaded but no candidates are linked to companies (company_id or establishment_code). Link candidates to companies to see points and activity here.'
              : 'No candidate or points data yet. Add companies and link candidates (company_id or establishment_code) to see the report.'}
          </p>
          {report.length > 0 && (
            <p style={{ color: '#64748b', margin: '16px 0 0 0', fontSize: 13 }}>
              {report.length} companies found. Zero candidates linked to any company.
            </p>
          )}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 900 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Company Name</th>
                <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Est. Code</th>
                <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Candidate Name</th>
                <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Mobile</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Points</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Games</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Quizzes</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Check-ins</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Stipend/Att.</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Referral</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Other</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Vouchers</th>
                <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Total Activity</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((r, idx) => (
                <tr
                  key={`${r.candidate_id}-${idx}`}
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: idx % 2 === 0 ? '#fff' : '#fafbfc'
                  }}
                >
                  <td style={{ padding: '12px 12px', fontWeight: 500, color: '#0f172a' }}>{r.company_name}</td>
                  <td style={{ padding: '12px 12px', color: '#64748b', fontSize: 13 }}>{r.establishment_code || '—'}</td>
                  <td style={{ padding: '12px 12px', fontWeight: 500, color: '#0f172a' }}>{r.name}</td>
                  <td style={{ padding: '12px 12px', color: '#64748b' }}>{r.mobile}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', fontWeight: 600, color: '#6366f1' }}>{r.total_points}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.games_played}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.quizzes_taken}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.check_ins}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.stipend_attendance}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.referral}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', color: '#475569' }}>{r.other}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', fontWeight: 600, color: '#9333ea' }}>{r.vouchers_claimed}</td>
                  <td style={{ padding: '12px 12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>{r.total_activities}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {flatRows.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginTop: 20,
          padding: '16px 20px',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#64748b' }}>
              Showing {startIdx + 1}–{Math.min(startIdx + pageSize, flatRows.length)} of {flatRows.length} candidates
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 13, color: '#64748b' }}>Rows per page:</label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 14,
                  color: '#334155',
                  cursor: 'pointer',
                  background: '#fff'
                }}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="gaming-pagination-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: page <= 1 ? '#f8fafc' : '#fff',
                color: page <= 1 ? '#94a3b8' : '#475569',
                fontWeight: 500,
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 14
              }}
            >
              ‹ Previous
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    type="button"
                    className={`gaming-pagination-page-btn ${page === pageNum ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                    style={{
                      minWidth: 36,
                      height: 36,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: page === pageNum ? 'none' : '1px solid #e2e8f0',
                      background: page === pageNum ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : '#fff',
                      color: page === pageNum ? '#fff' : '#475569',
                      fontWeight: page === pageNum ? 600 : 500,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="gaming-pagination-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: page >= totalPages ? '#f8fafc' : '#fff',
                color: page >= totalPages ? '#94a3b8' : '#475569',
                fontWeight: 500,
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                fontSize: 14
              }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .gaming-pagination-btn:not(:disabled):hover {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
        }
        .gaming-pagination-page-btn:not(.active):hover {
          background: #f1f5f9 !important;
        }
      `}</style>
    </div>
  );
}

export default AdminGamingReport;
